import { ref, computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useSurveyStore } from "@stores/survey/survey";
import useMyFetch from "@stores/useMyFetch";
import { useDeltaTracking, createSaveAll, type SaveErrorPayload } from "@utils/composables/useDeltaTracking";
import type { Section } from "@/types";

export const useSectionsStore = defineStore("sections", () => {
  const { token: surveyToken } = storeToRefs(useSurveyStore());
  const delta = useDeltaTracking();

  const sections = ref<Section[]>([]);

  const basePath = computed(() => `surveys/${surveyToken.value}/sections`);
  const hasUnsavedChanges = delta.hasUnsavedChanges;

  const fetch = async () => {
    if (!surveyToken.value) return;
    const { response, data } = await useMyFetch(basePath.value).get().json<Section[]>();
    sections.value = response.value?.ok ? (data.value ?? []) : [];
    delta.reset();
  };

  const findByToken = (token: string) => sections.value.find((section) => section.token === token) ?? null;

  const findIndex = (token: string) => sections.value.findIndex((section) => section.token === token);

  const predecessorToken = (token: string) => {
    const index = findIndex(token);
    return index > 0 ? sections.value[index - 1].token : null;
  };

  const addSection = (section: Section, afterToken: string | null = null) => {
    const index = afterToken ? findIndex(afterToken) : sections.value.length - 1;
    sections.value.splice(index + 1, 0, section);
    delta.markNew(section.token);
  };

  const renameSection = (token: string, name: string) => {
    const section = findByToken(token);
    if (!section) return;
    section.name = name;
    delta.markDirty(token);
  };

  const removeSection = (token: string) => {
    const index = findIndex(token);
    if (index <= 0) return;
    sections.value.splice(index, 1);
    delta.markDeleted(token);
  };

  const moveSection = (token: string, afterToken: string | null) => {
    const index = findIndex(token);
    if (index === -1) return;
    const [section] = sections.value.splice(index, 1);
    const targetIndex = afterToken ? findIndex(afterToken) : -1;
    sections.value.splice(targetIndex + 1, 0, section);
    delta.markMoved(token, { after_token: afterToken });
  };

  const saveAll = createSaveAll(delta, findIndex, {
    destroy: (token) => useMyFetch(`${basePath.value}/${token}`).delete().json<SaveErrorPayload>(),
    create: (token) => {
      const section = sections.value[findIndex(token)];
      return useMyFetch(basePath.value)
        .post({ section: { token: section.token, name: section.name, after_token: predecessorToken(token) } })
        .json<SaveErrorPayload>();
    },
    update: (token) =>
      useMyFetch(`${basePath.value}/${token}`)
        .patch({ section: { name: sections.value[findIndex(token)].name } })
        .json<SaveErrorPayload>(),
    reorder: (token) =>
      useMyFetch(`${basePath.value}/${token}/reorder`)
        .patch({ after_token: predecessorToken(token) })
        .json<SaveErrorPayload>(),
  });

  return {
    sections,
    hasUnsavedChanges,
    fetch,
    findByToken,
    findIndex,
    addSection,
    renameSection,
    removeSection,
    moveSection,
    saveAll,
    resetDirtyState: delta.reset,
  };
});
